import React from "react";
import { SmartLink } from "../smart-link";
import { plainText } from "../../lib/plain-text";
import { EmptyBlock } from "../empty-block";
import type { BaseComponentProps } from "../../types/puck";

export interface BrandItem {
  name: string;
  logoUrl?: string;
  href?: string;
}

export type BrandDisplayMode = "logos" | "names" | "mixed";
export type BrandChipStyle = "pill" | "rounded" | "sharp" | "none";
export type BrandSpacing = "tight" | "default" | "relaxed" | "loose";
export type BrandMediaStyle = "logo" | "cover";

export interface BrandMarqueeProps extends BaseComponentProps {
  heading?: string;
  subheading?: string;
  brands: BrandItem[];
  displayMode?: BrandDisplayMode;
  /** How uploaded images render: compact grayscale logo strip, or tall full-color cover art. */
  mediaStyle?: BrandMediaStyle;
  scrolling?: "yes" | "no";
  spacing?: BrandSpacing;
  chipStyle?: BrandChipStyle;
  shadow?: "yes" | "no";
  backgroundColor?: string;
}

const SPACING_PX: Record<BrandSpacing, number> = {
  tight: 24,
  default: 48,
  relaxed: 80,
  loose: 128,
};

function getChipClass(style: BrandChipStyle, shadow: boolean): string {
  const shadowPart = shadow ? " jobsite:shadow-sm" : "";
  switch (style) {
    case "pill":
      return `jobsite:rounded-full jobsite:border jobsite:border-neutral-200 jobsite:bg-white jobsite:px-5 jobsite:py-2${shadowPart}`;
    case "rounded":
      return `jobsite:rounded-md jobsite:border jobsite:border-neutral-200 jobsite:bg-white jobsite:px-4 jobsite:py-2${shadowPart}`;
    case "sharp":
      return `jobsite:border jobsite:border-neutral-200 jobsite:bg-white jobsite:px-4 jobsite:py-2${shadowPart}`;
    case "none":
      return "jobsite:px-2 jobsite:py-1";
  }
}

function BrandTile({
  brand,
  displayMode,
  mediaStyle,
  chipStyle,
  shadow,
}: {
  brand: BrandItem;
  displayMode: BrandDisplayMode;
  mediaStyle: BrandMediaStyle;
  chipStyle: BrandChipStyle;
  shadow: boolean;
}) {
  const hasLogo = !!brand.logoUrl?.trim();
  const showLogo = displayMode === "logos" || (displayMode === "mixed" && hasLogo);

  const inner = showLogo && hasLogo ? (
    mediaStyle === "cover" ? (
      <img
        src={brand.logoUrl}
        alt={brand.name}
        loading="lazy"
        className={`jobsite:h-32 jobsite:w-auto jobsite:max-w-[200px] jobsite:border jobsite:border-neutral-200 jobsite:object-contain jobsite:transition-transform jobsite:hover:scale-[1.03] jobsite:sm:h-40${shadow ? " jobsite:shadow-sm" : ""}`}
      />
    ) : (
      <img
        src={brand.logoUrl}
        alt={brand.name}
        loading="lazy"
        className="jobsite:h-12 jobsite:w-auto jobsite:max-w-[160px] jobsite:object-contain jobsite:opacity-80 jobsite:grayscale jobsite:transition-all jobsite:hover:opacity-100 jobsite:hover:grayscale-0"
      />
    )
  ) : (
    <span
      className={`${getChipClass(chipStyle, shadow)} jobsite:text-sm jobsite:font-medium jobsite:text-neutral-700`}
    >
      {brand.name}
    </span>
  );

  const wrapperClass = "jobsite:flex jobsite:shrink-0 jobsite:items-center";

  if (brand.href?.trim()) {
    return (
      <SmartLink href={brand.href} className={wrapperClass} aria-label={brand.name}>
        {inner}
      </SmartLink>
    );
  }
  return <div className={wrapperClass}>{inner}</div>;
}

export function BrandMarquee({
  id,
  heading,
  subheading,
  brands,
  displayMode = "mixed",
  mediaStyle = "logo",
  scrolling = "yes",
  spacing = "default",
  chipStyle = "pill",
  shadow = "yes",
  backgroundColor,
}: BrandMarqueeProps) {
  const visibleBrands = (brands || []).filter((b) => b?.name?.trim());
  if (visibleBrands.length === 0) {
    return <EmptyBlock label="No logos yet — add them in the panel" />;
  }

  const isScrolling = scrolling === "yes" && visibleBrands.length > 1;
  const animationDuration = Math.max(20, visibleBrands.length * 4);
  const columnGapPx = SPACING_PX[spacing] ?? SPACING_PX.default;
  const shadowOn = shadow !== "no";

  const items = visibleBrands.map((brand, i) => (
    <BrandTile
      key={`brand-${i}-${brand.name}`}
      brand={brand}
      displayMode={displayMode}
      mediaStyle={mediaStyle}
      chipStyle={chipStyle}
      shadow={shadowOn}
    />
  ));

  return (
    <section
      id={id}
      style={backgroundColor ? { backgroundColor } : undefined}
      className="jobsite:py-12"
    >
      <div className="jobsite:mx-auto jobsite:max-w-6xl jobsite:px-4">
        {(heading || subheading) && (
          <div className="jobsite:mb-8 jobsite:text-center">
            {heading && (
              <h2 className="jobsite:text-2xl jobsite:font-semibold jobsite:text-neutral-900 jobsite:sm:text-3xl">
                {heading}
              </h2>
            )}
            {subheading && (
              <p className="jobsite:mt-2 jobsite:text-neutral-600">{subheading}</p>
            )}
          </div>
        )}

        {isScrolling ? (
          <>
            <style>{`
              @keyframes brand-marquee-scroll {
                from { transform: translate3d(0, 0, 0); }
                to { transform: translate3d(-50%, 0, 0); }
              }
              .brand-marquee-track { animation: brand-marquee-scroll var(--brand-marquee-duration, 30s) linear infinite; }
              .brand-marquee-viewport:hover .brand-marquee-track { animation-play-state: paused; }
              @media (prefers-reduced-motion: reduce) {
                .brand-marquee-track { animation: none; transform: none; }
              }
            `}</style>
            <div
              className="brand-marquee-viewport jobsite:relative jobsite:overflow-hidden"
              style={{
                maskImage:
                  "linear-gradient(to right, transparent, black 6%, black 94%, transparent)",
                WebkitMaskImage:
                  "linear-gradient(to right, transparent, black 6%, black 94%, transparent)",
              }}
            >
              <div
                className="brand-marquee-track jobsite:flex jobsite:w-max jobsite:items-center jobsite:py-2"
                style={
                  {
                    columnGap: `${columnGapPx}px`,
                    ["--brand-marquee-duration" as string]: `${animationDuration}s`,
                  } as React.CSSProperties
                }
                aria-label={plainText(heading) || "Brands we service"}
              >
                {items}
                {items.map((item, i) =>
                  React.cloneElement(item, { key: `dup-${i}`, "aria-hidden": true }),
                )}
              </div>
            </div>
          </>
        ) : (
          <div
            className="jobsite:flex jobsite:flex-wrap jobsite:items-center jobsite:justify-center jobsite:gap-y-6"
            style={{ columnGap: `${columnGapPx}px` }}
          >
            {items}
          </div>
        )}
      </div>
    </section>
  );
}
